import { Sidepanel } from '@/components/layout/Sidepanel';
import { ContactEditPanel } from '@/features/employees/components/contact';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import { createContext, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateContact } from '../employees/components/createEmployeeForm/CreateContact';
import { JoinRequest } from '../employees/components/createEmployeeForm/JoinRequest';
import type { EmployeeSidePanelContent } from './types';

interface EmployeeSidePanelContextValue {
  open: (content: EmployeeSidePanelContent, companyId: string) => void;
  close: () => void;
  isOpen: boolean;
}

export const EmployeeSidePanelContext = createContext<EmployeeSidePanelContextValue | null>(null);

type ContentWithCompany = EmployeeSidePanelContent & { companyId: string };

interface EmployeeSidePanelProviderProps {
  children: ReactNode;
}

export function EmployeeSidePanelProvider({ children }: EmployeeSidePanelProviderProps) {
  const { t } = useTranslation();
  const sidePanel = useSidePanelHandler<ContentWithCompany>();

  const open = useCallback(
    (content: EmployeeSidePanelContent, companyId: string) => {
      sidePanel.open({ ...content, companyId } as ContentWithCompany);
    },
    [sidePanel]
  );

  const contextValue: EmployeeSidePanelContextValue = {
    open,
    close: sidePanel.close,
    isOpen: sidePanel.isOpen,
  };

  const getTitle = () => {
    if (!sidePanel.content) return '';
    switch (sidePanel.content.type) {
      case 'create':
        return t('employees.sidePanel.createTitle');
      case 'acceptJoinRequest':
        return t('employees.sidePanel.acceptJoinRequestTitle');
      case 'edit':
        return sidePanel.content.employee?.contact?.name || t('employees.sidePanel.editTitle');
    }
  };

  const getPanelWidth = () => {
/*     if (!sidePanel.content) return '';
    if (sidePanel.content.type === 'create' || sidePanel.content.type === 'acceptJoinRequest' || sidePanel.content.type === 'create') {
      return 'sm:w-[70vw] sm:min-w-[500px]';
    } */
    return 'sm:w-[70vw] sm:min-w-[500px]';
  };

  return (
    <EmployeeSidePanelContext.Provider value={contextValue}>
      {children}
      {sidePanel.content && sidePanel.panelKey && (
        <Sidepanel
          key={sidePanel.panelKey}
          isOpen={sidePanel.isOpen}
          onClose={sidePanel.close}
          title={getTitle()}
          className={getPanelWidth()}
          
        >
          {sidePanel.content.type === 'create' && (
            <CreateContact
              companyId={sidePanel.content.companyId}
              onSuccess={sidePanel.close}
              linkedUser={sidePanel.content.linkedUser}
            />
          )}
          {sidePanel.content.type === 'edit' && sidePanel.content.employee?.contact?.id && (
            <ContactEditPanel
              companyId={sidePanel.content.companyId}
              contactId={sidePanel.content.employee.contact.id}
              onClose={sidePanel.close}
            />
          )}
          {sidePanel.content.type === 'acceptJoinRequest' && (
            <JoinRequest
              companyId={sidePanel.content.companyId}
              onSuccess={sidePanel.close}
              joinRequest={sidePanel.content.joinRequest}
            />
          )}
        </Sidepanel>
      )}
    </EmployeeSidePanelContext.Provider>
  );
}
